import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { Alert, Button, Drawer, Icon, Loading } from '@ibk/shared-ui';
import { useEmissorContractAddressActions, useEmissorContractAddresses, useEmissorUpdateContractAddress, } from '../../hooks';
import { mapAddressToCreateContractAddressBody, mapAddressToUpdateContractAddressBody, mapEmissorContractAddressItemToAddress, } from '../../pages/NewRechargePage/components/EmissorDeliveryAddress/emissorDeliveryAddressMapper';
import { beneficiosTranslations } from '../../translations';
import { AddressModal } from '../AddressModal';
import styles from './EmissorAddressList.module.css';
import { EmissorAddressListItem } from './EmissorAddressListItem';
import { EmissorDeleteAddressModal } from './EmissorDeleteAddressModal';
function EmissorAddressListPanelContent({ isOpen, onClose, contractId, onPrimaryChange, testIdIdentifier = 'EmissorAddressListPanel', }) {
    const { t } = useTranslation();
    const [alert, setAlert] = useState(null);
    const [addressForm, setAddressForm] = useState(null);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [activatingAddressId, setActivatingAddressId] = useState(null);
    const { addresses, isLoading, isError, refetch } = useEmissorContractAddresses({ contractId, enabled: isOpen });
    const { createAddress, deleteAddress, activateAddress, isCreating, isDeleting, isActivating } = useEmissorContractAddressActions();
    const { updateAddress, isUpdating } = useEmissorUpdateContractAddress();
    const pendingPrimaryId = addresses.some(address => address.id === activatingAddressId && !address.isPrimary)
        ? activatingAddressId
        : null;
    const isSelectionBusy = isActivating || pendingPrimaryId !== null;
    useEffect(() => {
        if (activatingAddressId !== null && pendingPrimaryId === null) {
            setActivatingAddressId(null);
        }
    }, [activatingAddressId, pendingPrimaryId]);
    const showLoading = isLoading && addresses.length === 0;
    const showList = !showLoading && !isError;
    const editingAddress = addressForm?.address ?? null;
    const editingFormAddress = useMemo(() => mapEmissorContractAddressItemToAddress(editingAddress), [editingAddress]);
    const saveAction = editingAddress ? 'update' : 'create';
    const showAlert = (appearance, translationKey) => {
        setAlert({ appearance, message: t(translationKey) });
    };
    const notifyPrimaryChange = (address) => {
        if (address?.isPrimary) {
            onPrimaryChange?.(address);
        }
    };
    const handleClose = () => {
        setAlert(null);
        setAddressForm(null);
        setAddressToDelete(null);
        setActivatingAddressId(null);
        onClose();
    };
    const handleSelect = async (address) => {
        if (!contractId) {
            return;
        }
        setAlert(null);
        setActivatingAddressId(address.id);
        try {
            await activateAddress({ contractId, addressId: address.id });
            showAlert('success', 'address.emissor.success.update');
            notifyPrimaryChange({ ...address, isPrimary: true });
        }
        catch {
            setActivatingAddressId(null);
            showAlert('error', 'address.emissor.error.activate');
            refetch();
        }
    };
    const persistAddress = (currentContractId, address) => {
        if (editingAddress) {
            return updateAddress({
                contractId: currentContractId,
                addressId: editingAddress.id,
                body: mapAddressToUpdateContractAddressBody(address),
            });
        }
        return createAddress({ contractId: currentContractId, body: mapAddressToCreateContractAddressBody(address) });
    };
    const handleSave = async (address) => {
        if (!contractId) {
            return;
        }
        setAlert(null);
        const saved = await persistAddress(contractId, address);
        showAlert('success', `address.emissor.success.${saveAction}`);
        notifyPrimaryChange(saved);
    };
    const handleConfirmDelete = async () => {
        if (!contractId || !addressToDelete) {
            return;
        }
        setAlert(null);
        try {
            await deleteAddress({ contractId, addressId: addressToDelete.id });
            showAlert('success', 'address.emissor.success.delete');
        }
        catch {
            showAlert('error', 'address.emissor.error.delete');
            refetch();
        }
        finally {
            setAddressToDelete(null);
        }
    };
    const renderContent = () => {
        if (showLoading) {
            return (_jsx("div", { className: styles.panelState, "data-testid": `view${testIdIdentifier}Loading`, children: _jsx(Loading, { size: 'small' }) }));
        }
        if (isError) {
            return (_jsxs("div", { className: styles.panelState, "data-testid": `view${testIdIdentifier}Error`, children: [_jsx("span", { className: styles.panelStateIcon, children: _jsx(Icon, { name: 'location' }) }), _jsx("h3", { className: styles.panelStateTitle, "data-testid": `text${testIdIdentifier}ErrorTitle`, children: t('address.error.title') }), _jsx("p", { className: styles.panelStateDescription, "data-testid": `text${testIdIdentifier}ErrorDescription`, children: t('address.error.description') })] }));
        }
        return addresses.map((address, index) => (_jsx(EmissorAddressListItem, { address: address, onSelect: handleSelect, onEdit: selected => setAddressForm({ address: selected }), onDelete: setAddressToDelete, isSelectionDisabled: isSelectionBusy, isActivating: pendingPrimaryId === address.id, testIdIdentifier: `${testIdIdentifier}Item${index}` }, address.id)));
    };
    return (_jsxs(_Fragment, { children: [_jsx(Drawer, { isOpen: isOpen, onClose: handleClose, onReturn: handleClose, showCloseButton: false, closeOnBackdropClick: !addressForm && !addressToDelete, title: t('deliveryAddress'), customStyles: { 'drawer-body': styles.panelBody }, children: isOpen && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.panelContent, "data-testid": `view${testIdIdentifier}Content`, children: [alert && !pendingPrimaryId && (_jsx(Alert, { alertType: 'inline', appearance: alert.appearance, density: 'compact', alignItems: 'center', duration: 8, message: alert.message, onClose: () => setAlert(null), className: styles.alert, testIdIdentifier: `${testIdIdentifier}Alert` })), renderContent()] }), showList && (_jsx("div", { className: styles.panelFooter, children: _jsx(Button, { variant: 'primary', size: 'medium', onClick: () => setAddressForm({ address: null }), disabled: isSelectionBusy, dataTestId: `button${testIdIdentifier}AddAddress`, children: t('address.emissor.button.addAddress') }) }))] })) }), _jsx(AddressModal, { show: Boolean(addressForm), title: t('deliveryAddress'), onHide: () => setAddressForm(null), onSave: handleSave, isSaving: isCreating || isUpdating, currentAddress: editingFormAddress, saveErrorMessage: t(`address.emissor.error.${saveAction}`), testIdIdentifier: `modal${testIdIdentifier}AddressModal` }), _jsx(EmissorDeleteAddressModal, { isOpen: Boolean(addressToDelete), onClose: () => setAddressToDelete(null), onConfirm: handleConfirmDelete, isDeleting: isDeleting, dataTestId: `${testIdIdentifier}DeleteModal` })] }));
}
export function EmissorAddressListPanel(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(EmissorAddressListPanelContent, { ...props }) }));
}
